import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
  Alert,
  Box,
  Chip,
  useTheme,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import axiosInstance from '../../Pages/axiosInstance';
import posthog from 'posthog-js';

interface ReportRepairDialogProps {
  open: boolean;
  onClose: () => void;
  deviceNumber: string | number;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  onRepairReported?: () => void;
}

const ReportRepairDialog: React.FC<ReportRepairDialogProps> = ({
  open,
  onClose,
  deviceNumber,
  onSuccess,
  onError,
  onRepairReported
}) => {
  const theme = useTheme();
  const [repairComment, setRepairComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const handleClose = () => {
    setRepairComment('');
    setIsSubmitting(false);
    setShowStatusConfirmation(false);
    setStatusMessage('');
    onClose();
  };

  const handleSubmit = async (changeStatusToBiuroTest?: boolean) => {
    if (!repairComment.trim()) {
      onError?.('Please enter a repair comment');
      return;
    }

    setIsSubmitting(true);
    try {
      posthog.capture('service_comment_added', {
        username: localStorage.getItem('username'),
        device_number: deviceNumber,
        comment_length: repairComment.length,
        timestamp: new Date().toISOString()
      });

      const payload: any = {
        box: deviceNumber,
        comment: repairComment,
      };

      if (changeStatusToBiuroTest !== undefined) {
        payload.change_status_to_biuro_test = changeStatusToBiuroTest;
      }

      await axiosInstance.post('/servicepanel/report_service/', payload);

      onSuccess?.('Repair reported successfully');
      onRepairReported?.();
      handleClose();
    } catch (error: any) {
      console.error('Error reporting repair:', error);
      
      // Check if this is the status validation error
      if (error.response?.data?.detail === "Box ma status WYSŁANY. Czy ma zostać zmieniony status na BIURO-TEST czy pozostać WYSŁANY?") {
        setStatusMessage(error.response.data.detail);
        setShowStatusConfirmation(true);
        setIsSubmitting(false);
        return;
      }
      
      posthog.capture('service_comment_failed', {
        username: localStorage.getItem('username'),
        device_number: deviceNumber,
        error: error.response?.data?.detail || error.message,
        timestamp: new Date().toISOString()
      });
      
      onError?.(`Error reporting repair: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      setIsSubmitting(false);
    }
  };

  const handleStatusConfirmation = (changeStatusToBiuroTest: boolean) => {
    setShowStatusConfirmation(false);
    handleSubmit(changeStatusToBiuroTest);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        background: `linear-gradient(135deg, ${theme.palette.success.main}15, ${theme.palette.success.main}05)`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <BuildIcon color="success" sx={{ fontSize: 28 }} />
        <Box>
          <Box sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary' }}>
            Report Repair
          </Box>
          <Chip 
            label={`Device #${deviceNumber}`} 
            size="small" 
            color="success" 
            variant="outlined"
            sx={{ mt: 0.5, fontSize: '0.75rem' }}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <DialogContentText sx={{ mb: 3, fontSize: '0.95rem', color: 'text.secondary' }}>
          Please provide details about the repair work completed on this device.
        </DialogContentText>
        
        {showStatusConfirmation && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': { fontSize: 22 },
              '& .MuiAlert-message': { fontSize: '0.95rem' }
            }}
            icon={<InfoIcon />}
          >
            <Box sx={{ fontWeight: 500, mb: 0.5 }}>Status Change Confirmation</Box>
            {statusMessage}
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="Repair Details"
          placeholder="Describe the repair work performed..."
          fullWidth
          variant="outlined"
          value={repairComment}
          onChange={(e) => setRepairComment(e.target.value)}
          multiline
          minRows={4}
          disabled={isSubmitting || showStatusConfirmation}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.success.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.success.main,
              },
            },
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 2, 
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        {showStatusConfirmation ? (
          <>
            <Button 
              onClick={handleClose} 
              disabled={isSubmitting}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 80,
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleStatusConfirmation(false)} 
              disabled={isSubmitting}
              startIcon={<CancelIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 140,
              }}
            >
              Keep WYSŁANY
            </Button>
            <Button 
              onClick={() => handleStatusConfirmation(true)} 
              variant="contained"
              disabled={isSubmitting}
              startIcon={<CheckCircleIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 140,
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' },
              }}
            >
              Change to BIURO-TEST
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={handleClose} 
              disabled={isSubmitting}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 80,
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleSubmit()} 
              variant="contained"
              disabled={isSubmitting || !repairComment.trim()}
              startIcon={isSubmitting ? null : <BuildIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 120,
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Report Repair'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportRepairDialog; 